// Решетката на работния лист „Инвентаризация" („по-тиха таблица"): плоски
// клетки (id `stx-{продукт}-{колона}`, стрелковата навигация е на картата около
// нея), ЕДНА решетка с три блока — „За броене" (закачените), „Преброени",
// „Непреброени" — всеки с лента-заглавие (`StocktakeBlockBand`).
//
// Колоните: # · Продукт · Търсено · Намерено · Разлика · Ед. цена · Стойност.
// Мярката е В числата („48 бр"). Непреброеният ред показва САМО „Търсено" и
// празното „Намерено" — без тирета. „Ед. цена" и „Стойност" (нето на
// разликата, знакова) се появяват само при разлика: при излишък са полета
// (цената на новата партида; празна червена „цена *" без източник на цена), при
// липса — само за четене. Брутото е само в реда „Общо".
//
// Партида/срок на новата партида се питат сами само за продукт, който ги
// следи; иначе под името на реда с излишък стои „+ партида / срок", която
// отваря подреда при нужда (`batchStateOf`). Под десктоп ширина редовете са
// карти (StocktakeLineCards) със СЪЩИТЕ id-та на клетките. Логиката на
// редовете е в `useStocktakeRowsActions`.
//
// Над блоковете — „Приготвени рецепти" (StocktakeRecipeSection). Продукт, който
// е в тях, носи под „Намерено" чипа „+0,630 кг в рецепти" (разбивката — в
// подред), а съставка без насипно преброено — „преброй и извън рецептата (или
// 0)" под името (StocktakeInRecipes).
import { Fragment, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  EMPTY_ENTRY_CELL_CLASS,
  FLAT_CELL_CLASS,
  FLAT_CELL_ERROR_CLASS,
  LINE_GRID_CLASS,
  LINE_GRID_MIN_WIDTH_CLASS,
  STOCKTAKE_COLS,
  batchFieldId,
  cellId,
  differenceTextOf,
  differenceToneOf,
  formatQtyWithUnit,
  hasDifference,
  isRowCounted,
  signedValueInputOf,
  type StocktakeComputedRow,
} from "./stocktakeWorksheetShared";
import { formatUnitPrice } from "../../components/numbers";
import { TruncatedText } from "../../components/list/TruncatedText";
import {
  SHEET_HEADER_CELL,
  SHEET_HEADER_ROW,
  SHEET_INPUT,
  SHEET_NAME_CELL,
  SHEET_NAME_TEXT,
  SHEET_ROW,
} from "../../components/list/density";
import type { NumericCol } from "./useStocktakeRowsActions";
import { StocktakeLineCards } from "./StocktakeLineCards";
import { StocktakeTotalsRow, type StocktakeTotals } from "./StocktakeTotalsRow";
import { StocktakeBlockBand } from "./StocktakeBlockBand";
import { batchStateOf, type StocktakeBlock, type StocktakeBlockKey } from "./stocktakeView";
import { formatSignedMoney } from "./stocktakeMeta";
import { StocktakeRecipeGridRows, type StocktakeRecipeSectionProps } from "./StocktakeRecipeSection";
import { CountLooseHint, InRecipesBreakdown, InRecipesChip } from "./StocktakeInRecipes";

export type StocktakeTableProps = {
  // Непразните блокове в реда на екрана (броят в лентата = броят на чипа)
  blocks: StocktakeBlock[];
  // „Приготвени рецепти" над блоковете (няма рецепти — секцията я няма)
  recipes?: StocktakeRecipeSectionProps | null;
  substitutionProductIds: Set<number>; // участва във взаимозамяна (чип ⇄)
  appliedProductIds: Set<number>; // взаимозамяната вече е приложена
  isDesktop: boolean;
  onCellChange: (productId: number, col: NumericCol, value: string) => void;
  onCellBlur: (productId: number, col: NumericCol) => void;
  onBatchChange: (productId: number, value: string) => void;
  onExpiryChange: (productId: number, expiry: string) => void;
  // сумите за реда „Общо" в дъното на решетката
  totals: StocktakeTotals;
};

// Картите получават и отворените ръчно подредове „партида / срок" и
// разбивките „в рецепти"
export type StocktakeRowsViewProps = StocktakeTableProps & {
  batchOpenIds: ReadonlySet<number>;
  onOpenBatch: (productId: number) => void;
  recipeBreakdownIds: ReadonlySet<number>;
  onToggleRecipeBreakdown: (productId: number) => void;
};

export const StocktakeWorksheetTable = (props: StocktakeTableProps) => {
  const {
    blocks,
    recipes,
    substitutionProductIds,
    appliedProductIds,
    isDesktop,
    onCellChange,
    onCellBlur,
    onBatchChange,
    onExpiryChange,
    totals,
  } = props;
  const { t } = useTranslation();
  // Ръчно отворените подредове „партида / срок" (продукт без следене); „+
  // партида / срок" отваря подреда и фокусира партидата след рендера му
  const [batchOpenIds, setBatchOpenIds] = useState<ReadonlySet<number>>(new Set());
  const onOpenBatch = (productId: number) => {
    setBatchOpenIds((ids) => (ids.has(productId) ? ids : new Set(ids).add(productId)));
    requestAnimationFrame(() => document.getElementById(batchFieldId(productId))?.focus());
  };
  // Отворените разбивки „в рецепти" (чипът под „Намерено" ги отваря/затваря)
  const [recipeBreakdownIds, setRecipeBreakdownIds] = useState<ReadonlySet<number>>(new Set());
  const onToggleRecipeBreakdown = (productId: number) =>
    setRecipeBreakdownIds((ids) => {
      const next = new Set(ids);
      if (!next.delete(productId)) next.add(productId);
      return next;
    });

  // Под десктоп ширина — карти (същите id-та на клетките)
  if (!isDesktop)
    return (
      <StocktakeLineCards
        {...props}
        batchOpenIds={batchOpenIds}
        onOpenBatch={onOpenBatch}
        recipeBreakdownIds={recipeBreakdownIds}
        onToggleRecipeBreakdown={onToggleRecipeBreakdown}
      />
    );
  const hasRecipes = !!recipes && recipes.rows.length > 0;

  // Клетка с число: стойност/стил по колоната (както при листа „Доставка");
  // `required` — задължителната ед. цена на излишък без източник
  const numericInput = (
    row: StocktakeComputedRow,
    col: NumericCol,
    value: string,
    extraClass: string,
    options: { placeholder?: string; required?: { placeholder: string; hint: string } } = {},
  ) => {
    const { required } = options;
    return (
      <input
        id={cellId(row.product.id, STOCKTAKE_COLS[col])}
        className={`${
          col === "counted" && value === "" ? EMPTY_ENTRY_CELL_CLASS : FLAT_CELL_CLASS
        } ${SHEET_INPUT} min-w-0 flex-1 px-[9px] text-right font-sheet text-[13px] ${extraClass} ${
          required ? `${FLAT_CELL_ERROR_CLASS} placeholder:text-danger-ink/60` : ""
        }`}
        value={value}
        placeholder={required?.placeholder ?? options.placeholder}
        title={required?.hint}
        aria-invalid={required ? true : undefined}
        inputMode="decimal"
        data-rk={row.product.id}
        data-c={STOCKTAKE_COLS[col]}
        onChange={(e) => onCellChange(row.product.id, col, e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => onCellBlur(row.product.id, col)}
      />
    );
  };

  // Само за четене; празен текст = празна клетка (без тире)
  const readonlyCell = (text: string, extraClass = "") => (
    <div className="flex items-center justify-end border-r border-shade-2 px-[11px]">
      <span className={`whitespace-nowrap font-sheet text-[13px] leading-[normal] ${extraClass}`}>{text}</span>
    </div>
  );

  return (
    <div className={`${LINE_GRID_MIN_WIDTH_CLASS} ${blocks.length || hasRecipes ? "flex min-h-full flex-col" : ""}`}>
      {/* Заглавен ред (sticky в скрола на картата) */}
      <div
        className={`${LINE_GRID_CLASS} ${SHEET_HEADER_ROW} sticky top-0 z-[5] border-b border-shade-3 bg-fill`}
      >
        <Th center>#</Th>
        <Th>{t("inventoryPage.colProductName")}</Th>
        <Th right>{t("inventoryPage.colSystem")}</Th>
        <Th right>{t("inventoryPage.colCounted")}</Th>
        <Th right>{t("inventoryPage.colDiff")}</Th>
        <Th right>{t("inventoryPage.colUnitPrice")}</Th>
        <Th right>{t("inventoryPage.colValue")}</Th>
      </div>

      {/* Приготвените рецепти — над блоковете, в същата решетка и клавиатура */}
      {hasRecipes && <StocktakeRecipeGridRows {...recipes} />}

      {/* Трите блока на ЕДНА решетка, всеки с лента-заглавие. Лентите и
          редовете са ЕДИН плосък списък с ключ по продукт: редът, който минава
          в „Преброени", ЗАПАЗВА DOM възела и фокуса си (с обвивка по блок се
          монтираше наново и → от „Намерено" към цената изпускаше фокуса). */}
      {blocks.flatMap((block) => [
        <StocktakeBlockBand
          key={`band-${block.key}`}
          kind={block.key}
          count={block.count}
          total={block.total}
          pinnedFrom={block.pinnedFrom}
        />,
        ...block.rows.map((row, rowIndex) => renderRow(row, block.startNumber + rowIndex, block.key)),
      ])}
      {blocks.length > 0 && <StocktakeTotalsRow totals={totals} />}
    </div>
  );

  // Един ред от решетката (+ подредът „партида / срок" при нужда)
  function renderRow(row: StocktakeComputedRow, rowNumber: number, block: StocktakeBlockKey) {
    const product = row.product;
    const counted = isRowCounted(row.draft);
    const isSurplus = row.kind === "surplus";
    const batchState = batchStateOf(row, batchOpenIds);
    const batchError = isSurplus && !!product.trackBatch && !row.draft.batch.trim();
    const expiryError = isSurplus && !!product.trackExpiry && !row.draft.expiry;
    const background = block === "counted" ? "bg-panel" : "bg-surface";
    return (
      <Fragment key={product.id}>
        <div className={`${LINE_GRID_CLASS} ${SHEET_ROW} items-stretch border-b border-shade-2 ${background}`}>
          {/* # */}
          <div className="flex items-center justify-center border-r border-shade-2 bg-fill font-sheet text-[11px] font-semibold leading-[normal] text-muted">
            {rowNumber}
          </div>
          {/* ПРОДУКТ (+ „+ партида / срок" под името при излишък без следене) */}
          <div className={`${SHEET_NAME_CELL} border-r border-shade-2`}>
            <div className="flex min-w-0 items-center gap-2">
              <TruncatedText text={product.name} className={SHEET_NAME_TEXT} />
              {substitutionProductIds.has(product.id) && (
                <span
                  title={t("inventoryPage.subChipHint")}
                  className={`shrink-0 rounded-full px-[7px] text-[10px] leading-[14px] ${
                    appliedProductIds.has(product.id)
                      ? "bg-brand text-white"
                      : "bg-brand-tint text-brand-deep-ink"
                  }`}
                >
                  ⇄
                </span>
              )}
            </div>
            {batchState === "link" && (
              <button
                type="button"
                onClick={() => onOpenBatch(product.id)}
                className="cursor-pointer self-start border-none bg-transparent p-0 text-[11px] font-semibold leading-[13px] text-brand-deep-ink hover:underline"
              >
                {t("inventoryPage.stqBatchLink")}
              </button>
            )}
            {row.needsLooseCount && <CountLooseHint />}
          </div>
          {/* ТЪРСЕНО — с мярката */}
          {readonlyCell(formatQtyWithUnit(row.systemQty, row.decimals, row.unit), "text-body")}
          {/* НАМЕРЕНО (c=0) — полето (НАСИПНОТО) и мярката след него (скрита,
              докато е празно, за да не мърда полето при първата цифра); под
              него — „+0,630 кг в рецепти", ако продуктът е в приготвени рецепти */}
          <div className="flex flex-col gap-0.5 border-r border-shade-2 p-[2px] pr-2.5">
            {/* полето е колкото реда (flex-1 + stretch); чипът „в рецепти" под него го удължава */}
            <div className="flex flex-1 items-stretch gap-1">
              {numericInput(row, "counted", row.draft.counted, "font-semibold text-ink")}
              <span
                className={`shrink-0 self-center text-[11.5px] leading-[normal] text-muted ${counted && row.unit ? "" : "invisible"}`}
              >
                {row.unit}
              </span>
            </div>
            {row.inRecipesQty > 0 && (
              <InRecipesChip
                row={row}
                open={recipeBreakdownIds.has(product.id)}
                onToggle={() => onToggleRecipeBreakdown(product.id)}
              />
            )}
          </div>
          {/* РАЗЛИКА — празна при непреброен ред */}
          {readonlyCell(differenceTextOf(row), `font-semibold ${differenceToneOf(row)}`)}
          {/* ЕД. ЦЕНА и СТОЙНОСТ — само при разлика */}
          {row.priceEditable ? (
            <>
              <CellBox>
                {numericInput(
                  row,
                  "price",
                  row.priceInput,
                  row.priceAuto && !row.priceMissing ? "italic text-muted" : "text-ink",
                  {
                    required: row.priceMissing
                      ? {
                          placeholder: t("inventoryPage.stkPricePh"),
                          hint: t("inventoryPage.stkPriceRequiredHint"),
                        }
                      : undefined,
                  },
                )}
              </CellBox>
              <CellBox>
                {numericInput(row, "net", signedValueInputOf(row), "font-semibold text-ok-deep", {
                  placeholder: "—",
                })}
              </CellBox>
            </>
          ) : (
            <>
              {readonlyCell(hasDifference(row) ? formatUnitPrice(row.unitPrice) : "", "text-body")}
              {readonlyCell(
                hasDifference(row) ? formatSignedMoney(row.differenceNet) : "",
                "font-semibold text-danger-ink",
              )}
            </>
          )}
        </div>

        {/* Подред „партида / срок" на новата партида — отместен, за да се чете
            като част от реда. Етикетите са В полетата (както навсякъде в
            модула); „*" — продуктът ги следи и са задължителни. */}
        {batchState === "fields" && (
          <div
            className={`flex flex-wrap items-center gap-2 border-b border-shade-2 py-[5px] pl-[55px] pr-[14px] ${background}`}
          >
            <input
              id={batchFieldId(product.id)}
              className={`h-[26px] w-[170px] rounded-[6px] border bg-surface px-2 text-[12px] leading-[normal] text-body outline-none placeholder:text-ghost focus:border-brand focus:shadow-[0_0_0_3px_rgba(217,127,0,0.15)] ${
                batchError ? "border-danger" : "border-shade-3"
              }`}
              value={row.draft.batch}
              onChange={(e) => onBatchChange(product.id, e.target.value)}
              placeholder={`${t("inventoryPage.colBatch")}${product.trackBatch ? " *" : ""}`}
            />
            <DatePicker
              value={row.draft.expiry ? dayjs(row.draft.expiry) : null}
              onChange={(d) => onExpiryChange(product.id, d ? d.format("YYYY-MM-DD") : "")}
              format="DD.MM.YYYY"
              placeholder={`${t("inventoryPage.colExpiry")}${product.trackExpiry ? " *" : ""}`}
              status={expiryError ? "error" : undefined}
              size="small"
              className={`!h-[26px] w-[170px] !rounded-[6px] !text-[12px] ${
                expiryError ? FLAT_CELL_ERROR_CLASS : ""
              }`}
            />
          </div>
        )}
        {/* Разбивката „в рецепти" — отваря се от чипа под „Намерено" */}
        {row.inRecipesQty > 0 && recipeBreakdownIds.has(product.id) && (
          <InRecipesBreakdown
            row={row}
            className={`border-b border-shade-2 py-[5px] pl-[55px] pr-[14px] ${background}`}
          />
        )}
      </Fragment>
    );
  }
};

// ─── Помощни клетъчни компоненти ────────────────────────────────────────────
function Th({
  children,
  center,
  right,
}: {
  children?: React.ReactNode;
  center?: boolean;
  right?: boolean;
}) {
  return (
    <div
      className={`${SHEET_HEADER_CELL} border-r border-shade-2 ${
        center ? "!px-2 justify-center text-center" : right ? "justify-end text-right" : "justify-start text-left"
      }`}
    >
      <span className="truncate">{children}</span>
    </div>
  );
}

// Обвивка на редактируема клетка (padding 2 — полето опира почти от край до край)
function CellBox({ children }: { children: React.ReactNode }) {
  return <div className="flex border-r border-shade-2 p-[2px]">{children}</div>;
}

export default StocktakeWorksheetTable;
